import dayjs from 'dayjs'
import { IcsDuration, IcsRecurrenceRule, getDurationFromInterval, IcsDateObject, IcsEvent } from 'ts-ics'

import { PeriodMode, toIcsDateTime } from '@/entities/schedule'

import { getProfileTimezone } from '@/shared/composables/timezone'

import { DEFAULT_DURATION_MS, MAX_RECURRENCE_YEARS, MS_PER_DAY, TIME_SLOT_STEP_MS } from '../consts'
import { Repeat, ScheduleDateTimeInputPayload } from '../types'

import { matchesRepeatPeriodEnd, periodEndForRepeat } from './recurrence-until'
import {
  ensureTimeToDiffers,
  getDefaultScheduleTimeRange,
  isScheduleTimeUnset,
} from './schedule-time'

type IcsCalendarDate = {
  start: IcsDateObject
  duration: IcsDuration
  recurrenceRule?: IcsRecurrenceRule
}

const weekdaysStr = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const

const UI_TO_ICS_WEEKDAY: Record<number, number> = {
  1: 1, // Пн → Пн
  2: 2, // Вт → Вт
  3: 3, // Ср → Ср
  4: 4, // Чт → Чт
  5: 5, // Пт → Пт
  6: 6, // Сб → Сб
  7: 0, // Вс → Вс (ICS)
}

const ICS_TO_UI_WEEKDAY: Record<number, number> = {
  0: 7, // Вс (ICS) → Вс
  1: 1, // Пн → Пн
  2: 2, // Вт → Вт
  3: 3, // Ср → Ср
  4: 4, // Чт → Чт
  5: 5, // Пт → Пт
  6: 6, // Сб → Сб
}

const uiWeekdayToIndex = (uiValue: number): number => {
  return UI_TO_ICS_WEEKDAY[uiValue] ?? -1
}

const indexToUiWeekday = (index: number): number => {
  return ICS_TO_UI_WEEKDAY[index] ?? -1
}

const getEndForDuration = (dayStart: dayjs.Dayjs, timeFrom: number, timeTo: number): dayjs.Dayjs => {
  const endSameDay = dayStart.add(timeTo, 'millisecond')
  if (timeTo < timeFrom) {
    return dayStart.add(MS_PER_DAY + timeTo, 'millisecond')
  }
  return endSameDay
}

const toMonthDayInMonth = (monthStart: dayjs.Dayjs, monthday: number): dayjs.Dayjs | null => {
  const candidate = monthStart.date(monthday).startOf('day')
  if (candidate.month() !== monthStart.month()) {
    return null
  }
  return candidate
}

const parseMonthDayNumbers = (selectedDays: number[]): number[] => {
  return selectedDays
    .map(day => Number(day))
    .filter(day => !Number.isNaN(day) && day > 0)
}

const toStartOfDayInTz = (timestamp: number): dayjs.Dayjs => {
  const timezone = getProfileTimezone()
  return timestamp ? dayjs(timestamp).tz(timezone).startOf('day') : dayjs().tz(timezone).startOf('day')
}

const buildMaxUntil = (from: dayjs.Dayjs): IcsDateObject => (
  toIcsDateTime(periodEndForRepeat(from, 'always'))
)

const buildUntilByRepeat = (from: dayjs.Dayjs, repeat: Repeat): IcsDateObject => {
  if (repeat === 'always') {
    return buildMaxUntil(from)
  }
  return toIcsDateTime(periodEndForRepeat(from, repeat))
}

const inferRepeatFromUntil = (start: Date, until?: IcsDateObject): Repeat => {
  if (!until?.date) {
    return 'always'
  }

  const startDay = dayjs.tz(start)
  const untilDay = dayjs.tz(until.date)
  if (matchesRepeatPeriodEnd(untilDay, startDay, 1, 'week')) {
    return 'week'
  }
  if (matchesRepeatPeriodEnd(untilDay, startDay, 1, 'month')) {
    return 'month'
  }
  return 'always'
}

const isMaxRecurrenceUntil = (start: Date, until?: IcsDateObject) => {
  if (!until?.date) {
    return false
  }
  return matchesRepeatPeriodEnd(
    dayjs.tz(until.date),
    dayjs.tz(start),
    MAX_RECURRENCE_YEARS,
    'year',
  )
}

/** Первая дата >= startDate в месяце startDate (по выбранным числам), иначе — наименьший день в следующем месяце. */
const resolveSpecialMonthDayStart = (startDate: number, selectedDays: number[]): dayjs.Dayjs => {
  const referenceDay = toStartOfDayInTz(startDate)
  const referenceMonth = referenceDay.startOf('month')

  const monthdays = parseMonthDayNumbers(selectedDays)
  if (monthdays.length === 0) {
    return referenceDay
  }

  const sortedMonthdays = [...monthdays].sort((a, b) => a - b)

  for (const monthday of sortedMonthdays) {
    const candidate = toMonthDayInMonth(referenceMonth, monthday)
    if (candidate && !candidate.isBefore(referenceDay)) {
      return candidate
    }
  }

  const firstMonthday = sortedMonthdays[0]!
  const nextMonth = referenceMonth.add(1, 'month')
  return toMonthDayInMonth(nextMonth, firstMonthday) ?? nextMonth.startOf('day')
}

const resolveSelectedTimeRange = (
  selectedTime: ScheduleDateTimeInputPayload['selectedTime'],
): { timeFrom: number; timeTo: number } => {
  let timeFrom = selectedTime.timeFrom
  let timeTo = selectedTime.timeTo

  if (isScheduleTimeUnset(timeFrom) || isScheduleTimeUnset(timeTo)) {
    const defaults = getDefaultScheduleTimeRange()
    if (isScheduleTimeUnset(timeFrom)) {
      timeFrom = defaults.timeFrom
    }
    if (isScheduleTimeUnset(timeTo)) {
      timeTo = defaults.timeTo
    }
  }

  // timeFrom/timeTo === 0 — валидная полночь; совпадение «с» и «по» даёт нулевую длительность
  if (timeFrom === timeTo) {
    timeTo = (timeFrom + DEFAULT_DURATION_MS) % MS_PER_DAY
  }

  return {
    timeFrom,
    timeTo: ensureTimeToDiffers(timeFrom, timeTo, TIME_SLOT_STEP_MS),
  }
}

const getNowInProfileTz = () => dayjs().tz(getProfileTimezone())

const buildTimedOccurrence = (
  dayStart: dayjs.Dayjs,
  timeFrom: number,
  timeTo: number,
): { start: dayjs.Dayjs; duration: IcsDuration } => {
  const start = dayStart.add(timeFrom, 'millisecond')
  const end = getEndForDuration(dayStart, timeFrom, timeTo)
  return {
    start,
    duration: getDurationFromInterval(start.toDate(), end.toDate()),
  }
}

/**
 * WUI-4583: DTSTART — первая итерация. Если выбранное время уже прошло,
 * сдвигаем на ближайшую будущую итерацию (календарь + звонок не сегодня).
 */
const bumpDailyStartIfPast = (
  start: dayjs.Dayjs,
  intervalDays: number,
  now = getNowInProfileTz(),
): dayjs.Dayjs => {
  if (!start.isBefore(now)) {
    return start
  }

  const interval = Math.max(1, intervalDays || 1)
  let candidate = start
  for (let i = 0; i < 400 && candidate.isBefore(now); i++) {
    candidate = candidate.add(interval, 'day')
  }
  return candidate
}

const isWorkWeekday = (date: dayjs.Dayjs) => {
  const weekday = date.day()
  return weekday >= 1 && weekday <= 5
}

const bumpWorkDayStartIfPast = (
  start: dayjs.Dayjs,
  now = getNowInProfileTz(),
): dayjs.Dayjs => {
  let candidate = start
  for (let i = 0; i < 14; i++) {
    if (!candidate.isBefore(now) && isWorkWeekday(candidate)) {
      return candidate
    }
    candidate = candidate.add(1, 'day')
  }
  return candidate
}

const bumpWeekdayStartIfPast = (
  start: dayjs.Dayjs,
  selectedIcsWeekdays: number[],
  now = getNowInProfileTz(),
): dayjs.Dayjs => {
  if (!start.isBefore(now)) {
    return start
  }

  let candidate = start
  for (let i = 0; i < 14; i++) {
    candidate = candidate.add(1, 'day')
    if (selectedIcsWeekdays.includes(candidate.day()) && !candidate.isBefore(now)) {
      return candidate
    }
  }

  return start.add(7, 'day')
}

const bumpMonthDayStartIfPast = (
  start: dayjs.Dayjs,
  selectedDays: number[],
  now = getNowInProfileTz(),
): dayjs.Dayjs => {
  if (!start.isBefore(now)) {
    return start
  }

  const timeFrom = start.diff(start.startOf('day'))
  const monthdays = parseMonthDayNumbers(selectedDays)
  if (monthdays.length === 0) {
    return start.add(1, 'day')
  }

  let cursor = start.startOf('day').add(1, 'day')
  for (let i = 0; i < 370; i++) {
    if (monthdays.includes(cursor.date())) {
      const candidate = cursor.add(timeFrom, 'millisecond')
      if (!candidate.isBefore(now)) {
        return candidate
      }
    }
    cursor = cursor.add(1, 'day')
  }

  return start.add(1, 'month')
}

export const generateIcsEventRules = (periodMode: PeriodMode, payload: ScheduleDateTimeInputPayload): IcsCalendarDate => {
  const { timeFrom, timeTo } = resolveSelectedTimeRange(payload.selectedTime)
  const dayStart = toStartOfDayInTz(payload.startDate)
  const { start, duration } = buildTimedOccurrence(dayStart, timeFrom, timeTo)

  const defaultParameters = {
    duration,
    start: toIcsDateTime(start),
  }
  const selectedStartOfDay = toStartOfDayInTz(payload.startDate)

  switch (periodMode) {
  case PeriodMode.EVERY_DAY: {
    const interval = payload.repeatOptions.repeatDays || 1
    const bumpedStart = bumpDailyStartIfPast(start, interval)
    const bumped = buildTimedOccurrence(bumpedStart.startOf('day'), timeFrom, timeTo)

    return {
      duration: bumped.duration,
      start: toIcsDateTime(bumped.start),
      recurrenceRule: {
        frequency: 'DAILY',
        interval,
        until: buildUntilByRepeat(bumped.start, payload.repeatOptions.repeat),
      },
    }
  }
  case PeriodMode.EVERY_WORK_DAY: {
    const bumpedStart = bumpWorkDayStartIfPast(start)
    const bumped = buildTimedOccurrence(bumpedStart.startOf('day'), timeFrom, timeTo)

    return {
      duration: bumped.duration,
      start: toIcsDateTime(bumped.start),
      recurrenceRule: {
        frequency: 'WEEKLY',
        byDay: weekdaysStr.slice(1, 6).map(day => ({ day })),
        until: buildUntilByRepeat(bumped.start, payload.repeatOptions.repeat),
      },
    }
  }
  case PeriodMode.EVERY_SPECIAL_MONTH_DAY: {
    const byMonthday = parseMonthDayNumbers(payload.selectedDays)
    const monthDayStart = resolveSpecialMonthDayStart(payload.startDate, payload.selectedDays)
    const monthDayStartWithTime = bumpMonthDayStartIfPast(
      monthDayStart.add(timeFrom, 'millisecond'),
      payload.selectedDays,
    )
    const monthDayDayStart = monthDayStartWithTime.startOf('day')
    const monthDayEnd = getEndForDuration(monthDayDayStart, timeFrom, timeTo)
    const monthDayDuration = getDurationFromInterval(monthDayStartWithTime.toDate(), monthDayEnd.toDate())

    const recurrenceRule: IcsRecurrenceRule = {
      frequency: 'MONTHLY',
      byMonthday,
      until: payload.endDate
        ? toIcsDateTime(dayjs.tz(payload.endDate).endOf('day'))
        : buildMaxUntil(monthDayStartWithTime),
    }

    return {
      duration: monthDayDuration,
      start: toIcsDateTime(monthDayStartWithTime),
      recurrenceRule,
    }
  }
  case PeriodMode.EVERY_SPECIAL_WEEK_DAY:
    const validWeekdayIndices = payload.selectedWeekdays
      .map(uiWeekdayToIndex)
      .filter(index => index >= 0 && index < weekdaysStr.length)
      .map(index => ({ day: weekdaysStr[index]! }))

    if (validWeekdayIndices.length === 0) {
      return {
        ...defaultParameters,
      }
    }

    const referenceDate = toStartOfDayInTz(payload.startDate)
    const referenceWeekday = referenceDate.day() // 0 = воскресенье, 1 = понедельник, ..., 6 = суббота
    const selectedICSWeekdays = payload.selectedWeekdays
      .map(uiWeekdayToIndex)
      .filter(index => index >= 0 && index < weekdaysStr.length)

    let daysToAdd = 0

    if (selectedICSWeekdays.includes(referenceWeekday)) {
      daysToAdd = 0
    } else {
      let minDays = 7
      for (const weekday of selectedICSWeekdays) {
        let diff = weekday - referenceWeekday
        if (diff < 0) {
          diff += 7
        }
        if (diff < minDays) {
          minDays = diff
          daysToAdd = diff
        }
      }
    }

    const startDate = referenceDate.add(daysToAdd, 'day').startOf('day')
    const startDateTime = bumpWeekdayStartIfPast(
      startDate.add(timeFrom, 'millisecond'),
      selectedICSWeekdays,
    )
    const weekdayDayStart = startDateTime.startOf('day')
    const endDateTime = getEndForDuration(weekdayDayStart, timeFrom, timeTo)
    const durationForWeekday = getDurationFromInterval(startDateTime.toDate(), endDateTime.toDate())

    return {
      duration: durationForWeekday,
      start: toIcsDateTime(startDateTime),
      recurrenceRule: {
        frequency: 'WEEKLY',
        byDay: validWeekdayIndices,
        until: buildUntilByRepeat(startDateTime, payload.repeatOptions.repeat),
      },
    }
  default:
    return {
      ...defaultParameters,
      start: toIcsDateTime(selectedStartOfDay.add(payload.selectedTime.timeFrom || 0, 'millisecond')),
    }
  }
}

export const parseIcsEventRules = (event: IcsEvent): { periodMode: PeriodMode, payload: ScheduleDateTimeInputPayload } => {
  const payload: ScheduleDateTimeInputPayload = {
    startDate: 0,
    endDate: 0,
    selectedDays: [],
    selectedWeekdays: [],
    selectedTime: {
      timeFrom: 0,
      timeTo: 0,
    },
    repeatOptions: {
      repeat: 'week',
      repeatDays: 1,
    },
    summary: event.summary,
  }

  const dateFrom = dayjs(event.start.date).tz(getProfileTimezone())
  payload.startDate = dateFrom.startOf('day').valueOf()

  payload.selectedTime = (() => {
    const timeFrom = dateFrom.diff(dateFrom.startOf('day'))

    const resolveTimeToFromDuration = (duration: IcsDuration): number => {
      let deltaTime = dateFrom
      if (duration.weeks) {
        deltaTime = deltaTime.add(duration.weeks, 'week')
      }
      if (duration.days) {
        deltaTime = deltaTime.add(duration.days, 'day')
      }
      if (duration.hours) {
        deltaTime = deltaTime.add(duration.hours, 'hour')
      }
      if (duration.minutes) {
        deltaTime = deltaTime.add(duration.minutes, 'minute')
      }
      if (duration.seconds) {
        deltaTime = deltaTime.add(duration.seconds, 'second')
      }

      let timeTo = deltaTime.diff(dateFrom.startOf('day'))
      if (timeTo >= MS_PER_DAY) {
        timeTo = timeTo % MS_PER_DAY
      }

      return ensureTimeToDiffers(timeFrom, timeTo, TIME_SLOT_STEP_MS)
    }

    if (event.duration) {
      return {
        timeFrom,
        timeTo: resolveTimeToFromDuration(event.duration),
      }
    }

    // После ICS-экспорта duration часто заменён на end (DTEND)
    if (event.end?.date) {
      return {
        timeFrom,
        timeTo: resolveTimeToFromDuration(
          getDurationFromInterval(event.start.date, event.end.date),
        ),
      }
    }

    const fallbackTimeTo = (timeFrom + DEFAULT_DURATION_MS) % MS_PER_DAY
    return {
      timeFrom,
      timeTo: ensureTimeToDiffers(timeFrom, fallbackTimeTo, TIME_SLOT_STEP_MS),
    }
  })()

  const { recurrenceRule } = event

  payload.repeatOptions.repeatDays = recurrenceRule?.interval || 1
  payload.repeatOptions.repeat = inferRepeatFromUntil(event.start.date, recurrenceRule?.until)

  switch (recurrenceRule?.frequency) {
  case 'WEEKLY':
    const weekdays = recurrenceRule.byDay
    if (!weekdays) {
      return { periodMode: PeriodMode.EVERY_SPECIAL_WEEK_DAY, payload }
    }

    payload.selectedWeekdays = weekdays
      .map(w => {
        const day = w.day
        if (!day || typeof day !== 'string') {
          return -1
        }
        const index = weekdaysStr.indexOf(day as typeof weekdaysStr[number])
        return index >= 0 ? indexToUiWeekday(index) : -1
      })
      .filter(w => w > 0 && w <= 7)

    const isPeriodEveryWorkDay = weekdays.length === 5 && payload.selectedWeekdays.reduce((acc: boolean | null, val: number) => {
      if (!acc) {
        return false
      } else {
        return val > 0 && val < 6
      }
    }, true)

    if (isPeriodEveryWorkDay) {
      return { periodMode: PeriodMode.EVERY_WORK_DAY, payload }
    } else {
      return { periodMode: PeriodMode.EVERY_SPECIAL_WEEK_DAY, payload }
    }
  case 'MONTHLY':
    const byMonthday = recurrenceRule.byMonthday
    if (!byMonthday) {
      return { periodMode: PeriodMode.EVERY_SPECIAL_MONTH_DAY, payload }
    }
    payload.selectedDays = byMonthday
    if (recurrenceRule.until && !isMaxRecurrenceUntil(event.start.date, recurrenceRule.until)) {
      payload.endDate = dayjs.tz(recurrenceRule.until.date).startOf('day').valueOf()
      payload.repeatOptions.repeat = 'month'
    }
    return { periodMode: PeriodMode.EVERY_SPECIAL_MONTH_DAY, payload }
  case 'DAILY':
    return { periodMode: PeriodMode.EVERY_DAY, payload }
  default:
    return { periodMode: PeriodMode.ONCE, payload }
  }
}
